import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useCompetitionCandidates, useCompetitionCategories } from '@/features/competition/api'
import { usePublicMenu } from '@/features/food/api'
import { useOnsiteConfig } from '@/features/onsite/api'
import { usePublicProgram } from '@/features/program/api'
import { usePublicVoteCategories } from '@/features/voting/api'

import { useEventContent, useEventTeam, usePublicEvent } from './api'

export interface RubricFlags {
  menu: boolean
  program: boolean
  competition: boolean
  /** Les résultats ne s'ouvrent que le soir J, avec le premier scrutin. */
  votes: boolean
  /** Il y a des infos pratiques à lire — au-delà de ce que l'accueil montre. */
  info: boolean
  /** La commande à table, ouverte au micro le soir J. */
  onsite: boolean
  /** L'équipe LeYAMO — n'apparaît que si quelqu'un y a été ajouté. */
  team: boolean
  /** Les candidats validés. Rien à montrer tant qu'aucun n'est approuvé. */
  candidates: boolean
  /** « Votre avis » : recueil ouvert, et la soirée a commencé. */
  feedback: boolean
}

/**
 * Quelles rubriques existent réellement, et le menu qui y mène.
 *
 * Une seule source pour tout le site public : l'accueil comme chaque page de
 * rubrique appelle ce hook, si bien qu'aucune barre de navigation ne peut
 * diverger d'une autre. Les requêtes sont celles de React Query, donc
 * partagées en cache : les six pages ne provoquent pas six fois les mêmes
 * appels.
 *
 * Une rubrique vide n'apparaît pas. Proposer « Carte » pour arriver sur une
 * page blanche est pire que ne rien proposer — et, l'édition se remplissant
 * progressivement, cet état vide est la normale pendant des semaines.
 */
export function useRubrics() {
  const { t, i18n } = useTranslation()

  const eventQuery = usePublicEvent()
  const slug = eventQuery.data?.slug

  const menuQuery = usePublicMenu(slug)
  const programQuery = usePublicProgram(slug)
  const competitionQuery = useCompetitionCategories(slug)
  const candidatesQuery = useCompetitionCandidates(slug)
  const voteQuery = usePublicVoteCategories(slug)
  const contentQuery = useEventContent(slug)
  const onsiteQuery = useOnsiteConfig(slug)
  const teamQuery = useEventTeam(slug)

  /**
   * L'interrupteur de publication de la page.
   *
   * Absent = publié. Une édition qui n'a jamais ouvert l'écran des réglages
   * doit voir son site fonctionner ; c'est le refus explicite qui se déclare,
   * pas l'autorisation.
   */
  const published = (page: string): boolean =>
    eventQuery.data?.settings?.[`page.${page}.published`] !== false

  // Deux conditions, et les deux doivent tenir : la page a quelque chose à
  // montrer, ET l'équipe l'a laissée publiée. Publier une rubrique vide ne la
  // fait donc pas apparaître — proposer « Carte » pour arriver sur une page
  // blanche reste pire que ne rien proposer.
  const flags: RubricFlags = {
    menu: (menuQuery.data?.items.length ?? 0) > 0 && published('menu'),
    program: (programQuery.data ?? []).length > 0 && published('program'),
    competition: (competitionQuery.data ?? []).length > 0 && published('competition'),
    // Une catégorie encore fermée n'a rien à montrer : la rubrique
    // « Résultats » n'apparaît qu'à l'ouverture du premier vote, le soir J.
    votes:
      (voteQuery.data ?? []).some((category) => category.status !== 'closed') &&
      published('votes'),
    // L'accueil montre les deux premiers blocs : « Infos » n'a de raison
    // d'être que s'il en reste d'autres.
    info: (contentQuery.data ?? []).length > 2 && published('info'),
    onsite: onsiteQuery.data?.enabled ?? false,
    team: (teamQuery.data ?? []).length > 0 && published('team'),
    // Une candidature déposée n'est pas une candidature retenue : l'API ne
    // rend que les approuvées, et la rubrique n'existe qu'à partir de là.
    candidates: (candidatesQuery.data ?? []).length > 0 && published('competition'),
    // Avant la soirée, il n'y a rien à juger : le lien reste valable (QR,
    // e-mail), mais le menu ne le propose qu'une fois la soirée commencée.
    feedback:
      eventQuery.data?.settings?.['feedback.open'] !== false &&
      Boolean(eventQuery.data?.starts_at) &&
      new Date(eventQuery.data!.starts_at).getTime() <= Date.now(),
  }

  const navItems = useMemo(() => {
    const items: { to: string; label: string }[] = []
    // En tête : le soir J, commander passe avant tout le reste.
    if (flags.onsite) items.push({ to: '/commander', label: t('nav.onsite') })
    if (flags.menu) items.push({ to: '/carte', label: t('nav.menu') })
    if (flags.program) items.push({ to: '/programme', label: t('nav.programme') })
    if (flags.competition) items.push({ to: '/concours', label: t('nav.competition') })
    if (flags.candidates) items.push({ to: '/candidats', label: t('nav.candidates') })
    if (flags.votes) items.push({ to: '/resultats', label: t('nav.results') })
    if (flags.info) items.push({ to: '/infos', label: t('nav.info') })
    if (flags.team) items.push({ to: '/equipe', label: t('nav.team') })
    if (flags.feedback) items.push({ to: '/avis', label: t('nav.feedback') })
    return items
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    flags.menu,
    flags.program,
    flags.competition,
    flags.candidates,
    flags.votes,
    flags.info,
    flags.onsite,
    flags.team,
    flags.feedback,
    t,
    i18n.language,
  ])

  return { eventQuery, event: eventQuery.data, flags, navItems }
}
